import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useCms } from '../contexts/CmsContext';
import Header from './Header';
import Footer from './Footer';
import ScrollAnimation from './ScrollAnimation';
import CountUpAnimation from './CountUpAnimation';
import { Link } from 'react-router-dom';
import {
  MdVerifiedUser,
  MdArrowForward,
  MdRequestQuote,
  MdLocalShipping,
  MdWorkspacePremium,
  MdVerified,
  MdHeadsetMic,
  MdVisibility,
  MdTrackChanges,
  MdHub,
  MdInventory2,
  MdFactory,
  MdBolt,
  MdFoundation,
  MdSend,
  MdCall
} from 'react-icons/md';
import { BiCylinder } from 'react-icons/bi';

const Home = () => {
  const { language } = useLanguage();
  const { getContent } = useCms();
  const t = getContent(language);
  const h = t.home;
  const isRTL = language === 'ar';

  return (
    <div dir={isRTL ? 'rtl' : 'ltr'} className={`${isRTL ? 'font-cairo' : 'font-sans'} bg-background-light dark:bg-background-dark text-slate-900 dark:text-slate-100 transition-colors duration-300 ${isRTL ? 'rtl' : 'ltr'} animate-fade-in`}>
      <Header />
      
      {/* Hero Section */}
      <section className="relative h-[70vh] min-h-[550px] overflow-hidden flex items-center bg-slate-900 pt-24">
        <div className="absolute inset-0 flex overflow-x-auto snap-x snap-mandatory hide-scrollbar">
          <div className="flex-none w-full h-full snap-start relative">
            <img 
              alt="Industrial Construction" 
              className="absolute inset-0 w-full h-full object-cover" 
              src={h.hero.bgImage} 
            />
            <div className="absolute inset-0 bg-gradient-to-r from-primary/95 via-primary/60 to-primary/30"></div>
          </div>
        </div>
        <div className="relative z-10 max-w-[1440px] mx-auto px-6 lg:px-12 w-full">
          <div className="max-w-3xl pt-4 md:pt-8">
            <div className="inline-flex items-center gap-3 px-4 py-2 bg-secondary/20 backdrop-blur-md rounded-full mb-6 border border-secondary/30">
              <MdVerifiedUser className="text-secondary text-sm" />
              <span className={`text-white text-[10px] font-bold ${isRTL ? '' : 'tracking-[0.2em] uppercase'}`}>{h.hero.badge}</span>
            </div>
            <h1 className="text-white text-4xl md:text-6xl lg:text-7xl font-black mb-6 leading-[1.1]">
              {h.hero.titleLine1} <br />
              <span className="text-secondary">{h.hero.titleEmphasis}</span> {h.hero.titleLine2}
            </h1>
            <p className="text-lg md:text-xl text-slate-100 mb-8 max-w-2xl font-light leading-relaxed opacity-90">
              {h.hero.subtitle}
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link 
                to="/service" 
                className="px-8 py-3 bg-secondary hover:bg-[#c49b2d] text-slate-900 rounded-lg font-extrabold text-base transition-all shadow-2xl flex items-center justify-center gap-2 group"
              >
                {h.hero.explore}
                <MdArrowForward className={`transition-transform ${isRTL ? 'group-hover:-translate-x-1 rotate-180' : 'group-hover:translate-x-1'}`} />
              </Link>
              <Link 
                to="/contact" 
                className="px-8 py-3 bg-white/10 hover:bg-white/20 backdrop-blur-md border border-white/30 text-white rounded-lg font-bold text-base transition-all flex items-center justify-center gap-2"
              >
                <MdRequestQuote />
                {h.hero.requestQuote}
              </Link>
            </div>
          </div>
        </div>
        <div className={`absolute bottom-12 ${isRTL ? 'left-12' : 'right-12'} hidden lg:flex items-center gap-8 opacity-60 hover:opacity-100 transition-opacity`}>
          <img alt="Vision 2030" className="h-20 w-auto brightness-0 invert" src={h.hero.visionLogo} />
        </div>
      </section>

      {/* Badges Section */}
      <section className="py-12 bg-white dark:bg-slate-950 border-b border-slate-100 dark:border-slate-800">
        <div className="max-w-7xl mx-auto px-6">
          <ScrollAnimation animation="fade-in" className="flex flex-wrap justify-between gap-8 opacity-60">
            <div className="flex items-center gap-2 font-bold text-xs uppercase tracking-widest">
              <MdLocalShipping className="text-primary text-xl" /> {h.badges.delivery}
            </div>
            <div className="flex items-center gap-2 font-bold text-xs uppercase tracking-widest">
              <MdWorkspacePremium className="text-primary text-xl" /> {h.badges.certified}
            </div>
            <div className="flex items-center gap-2 font-bold text-xs uppercase tracking-widest">
              <MdVerified className="text-primary text-xl" /> {h.badges.iso}
            </div>
            <div className="flex items-center gap-2 font-bold text-xs uppercase tracking-widest">
              <MdHeadsetMic className="text-primary text-xl" /> {h.badges.support}
            </div>
          </ScrollAnimation>
        </div>
      </section>

      {/* About Section */}
      <section className="py-32 bg-background-light dark:bg-background-dark" id="about">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <ScrollAnimation animation="fade-up" className="grid lg:grid-cols-2 gap-24 items-center">
            <div className="order-2 lg:order-1 space-y-10">
              <div className="space-y-4">
                <h2 className="text-sm font-extrabold text-accent uppercase tracking-[0.3em]">{h.about.label}</h2>
                <h3 className="text-5xl lg:text-6xl font-black text-slate-900 dark:text-white leading-tight">{h.about.title}</h3>
              </div>
              <p className="text-xl text-slate-600 dark:text-slate-400 leading-relaxed font-light">
                {h.about.description}
              </p>
              <div className="grid sm:grid-cols-2 gap-8">
                <div className="p-8 bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-slate-100 dark:border-slate-700">
                  <MdVisibility className="text-4xl text-primary mb-4" />
                  <h4 className="font-bold text-xl mb-3">{h.about.vision.title}</h4>
                  <p className="text-slate-500 text-sm leading-relaxed">{h.about.vision.text}</p>
                </div>
                <div className="p-8 bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-slate-100 dark:border-slate-700">
                  <MdTrackChanges className="text-4xl text-secondary mb-4" />
                  <h4 className="font-bold text-xl mb-3">{h.about.mission.title}</h4>
                  <p className="text-slate-500 text-sm leading-relaxed">{h.about.mission.text}</p>
                </div>
              </div>
            </div>
            <div className="order-1 lg:order-2 relative">
              <div className="absolute -inset-4 bg-primary/5 rounded-[2rem] -rotate-3"></div>
              <img
                alt="AMHBM Operations"
                className="rounded-[1.5rem] shadow-3xl relative z-10 w-full object-cover aspect-square"
                src={h.about.image}
              />
              <div
                className={`absolute bottom-4 sm:-bottom-8 lg:-bottom-12 ${
                  isRTL ? 'right-4 sm:-right-8 lg:-right-12' : 'left-4 sm:-left-8 lg:-left-12'
                } bg-primary text-white p-6 sm:p-8 lg:p-10 rounded-2xl shadow-2xl z-20 max-w-[80%]`}
              >
                <p className="text-4xl sm:text-5xl lg:text-6xl font-black mb-1">
                  <CountUpAnimation end={h.about.legacyYears} suffix={isRTL ? '' : '+'} prefix={isRTL ? '+' : ''} />
                </p>
                <p className={`text-[10px] sm:text-xs font-bold ${isRTL ? '' : 'uppercase tracking-[0.2em]'} opacity-80`}>
                  {h.about.legacyText}
                </p>
              </div>
            </div>
          </ScrollAnimation>
        </div>
      </section>

      {/* Solutions Section */}
      <section className="py-32 bg-white dark:bg-slate-900" id="solutions">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <ScrollAnimation animation="fade-right" className="flex flex-col md:flex-row justify-between items-end mb-20 gap-8">
            <div className="max-w-3xl">
              <h2 className={`text-sm font-extrabold text-accent ${isRTL ? '' : 'uppercase tracking-[0.3em]'} mb-4`}>{h.solutions.label}</h2>
              <h3 className="text-5xl lg:text-6xl font-black text-slate-900 dark:text-white leading-tight">{h.solutions.title}</h3>
            </div>
            <p className="text-lg text-slate-500 dark:text-slate-400 max-w-md pb-2">
              {h.solutions.description}
            </p>
          </ScrollAnimation>
          <ScrollAnimation animation="fade-up" delay={200} className="grid grid-cols-1 md:grid-cols-3 gap-10 mb-24">
            {/* Card 1 */}
            <Link to="/service" className="group relative block h-[600px] rounded-3xl overflow-hidden shadow-2xl transition-all duration-700 hover:-translate-y-2">
              <img alt="Pipes & Tubing" className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-1000" src={h.solutions.categories.pipes.image} />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/40 to-transparent opacity-90 group-hover:opacity-100 transition-opacity"></div>
              <div className={`absolute bottom-10 ${isRTL ? 'right-10 left-10' : 'left-10 right-10'} text-white z-10`}>
                <span className={`inline-block px-3 py-1 bg-secondary text-slate-900 text-[10px] font-black ${isRTL ? '' : 'uppercase tracking-widest'} rounded mb-4`}>{h.solutions.categories.pipes.badge}</span>
                <h4 className="text-4xl font-bold mb-4">{h.solutions.categories.pipes.title}</h4>
                <p className="text-slate-200 text-lg opacity-0 group-hover:opacity-100 transition-all duration-500 translate-y-4 group-hover:translate-y-0">{h.solutions.categories.pipes.desc}</p>
              </div>
            </Link>
            {/* Card 2 */}
            <Link to="/service" className="group relative block h-[600px] rounded-3xl overflow-hidden shadow-2xl transition-all duration-700 hover:-translate-y-2">
              <img alt="Valves" className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-1000" src={h.solutions.categories.valves.image} />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/40 to-transparent opacity-90 group-hover:opacity-100 transition-opacity"></div>
              <div className={`absolute bottom-10 ${isRTL ? 'right-10 left-10' : 'left-10 right-10'} text-white z-10`}>
                <span className={`inline-block px-3 py-1 bg-accent text-white text-[10px] font-black ${isRTL ? '' : 'uppercase tracking-widest'} rounded mb-4`}>{h.solutions.categories.valves.badge}</span>
                <h4 className="text-4xl font-bold mb-4">{h.solutions.categories.valves.title}</h4>
                <p className="text-slate-200 text-lg opacity-0 group-hover:opacity-100 transition-all duration-500 translate-y-4 group-hover:translate-y-0">{h.solutions.categories.valves.desc}</p>
              </div>
            </Link>
            {/* Card 3 */}
            <Link to="/service" className="group relative block h-[600px] rounded-3xl overflow-hidden shadow-2xl transition-all duration-700 hover:-translate-y-2">
              <img alt="Electrical Systems" className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-1000" src={h.solutions.categories.electrical.image} />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/40 to-transparent opacity-90 group-hover:opacity-100 transition-opacity"></div>
              <div className={`absolute bottom-10 ${isRTL ? 'right-10 left-10' : 'left-10 right-10'} text-white z-10`}>
                <span className={`inline-block px-3 py-1 bg-primary text-white text-[10px] font-black ${isRTL ? '' : 'uppercase tracking-widest'} rounded mb-4`}>{h.solutions.categories.electrical.badge}</span>
                <h4 className="text-4xl font-bold mb-4">{h.solutions.categories.electrical.title}</h4>
                <p className="text-slate-200 text-lg opacity-0 group-hover:opacity-100 transition-all duration-500 translate-y-4 group-hover:translate-y-0">{h.solutions.categories.electrical.desc}</p>
              </div>
            </Link>
          </ScrollAnimation>
          
          {/* Services Content */}
          <ScrollAnimation animation="fade-up" className="grid lg:grid-cols-2 gap-20 items-center bg-slate-50 dark:bg-slate-800/50 p-12 lg:p-20 rounded-[3rem]">
            <div>
              <h4 className="text-3xl font-bold mb-8">{h.solutions.services.title}</h4>
              <div className="space-y-10">
                <div className="flex gap-6">
                  <div className="flex-shrink-0 w-14 h-14 bg-white dark:bg-slate-800 rounded-xl shadow-md flex items-center justify-center text-primary">
                    <MdHub className="text-3xl" />
                  </div>
                  <div>
                    <h5 className="text-xl font-bold mb-2">{h.solutions.services.procurement.title}</h5>
                    <p className="text-slate-500 dark:text-slate-400">{h.solutions.services.procurement.text}</p>
                  </div>
                </div>
                <div className="flex gap-6">
                  <div className="flex-shrink-0 w-14 h-14 bg-white dark:bg-slate-800 rounded-xl shadow-md flex items-center justify-center text-primary">
                    <MdInventory2 className="text-3xl" />
                  </div>
                  <div>
                    <h5 className="text-xl font-bold mb-2">{h.solutions.services.logistics.title}</h5>
                    <p className="text-slate-500 dark:text-slate-400">{h.solutions.services.logistics.text}</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div className="md:aspect-square bg-white dark:bg-slate-800 rounded-3xl p-8 flex flex-col items-center justify-center text-center hover:bg-primary hover:text-white transition-colors group cursor-default shadow-sm border border-slate-100 dark:border-slate-700">
                <BiCylinder className="text-5xl mb-4 text-primary group-hover:text-white" />
                <span className="font-bold uppercase tracking-wider text-xs">{h.solutions.services.industries.oil}</span>
              </div>
              <div className="md:aspect-square bg-white dark:bg-slate-800 rounded-3xl p-8 flex flex-col items-center justify-center text-center hover:bg-primary hover:text-white transition-colors group cursor-default shadow-sm border border-slate-100 dark:border-slate-700">
                <MdFactory className="text-5xl mb-4 text-primary group-hover:text-white" />
                <span className="font-bold uppercase tracking-wider text-xs">{h.solutions.services.industries.industry}</span>
              </div>
              <div className="md:aspect-square bg-white dark:bg-slate-800 rounded-3xl p-8 flex flex-col items-center justify-center text-center hover:bg-primary hover:text-white transition-colors group cursor-default shadow-sm border border-slate-100 dark:border-slate-700">
                <MdBolt className="text-5xl mb-4 text-primary group-hover:text-white" />
                <span className="font-bold uppercase tracking-wider text-xs">{h.solutions.services.industries.energy}</span>
              </div>
              <div className="md:aspect-square bg-white dark:bg-slate-800 rounded-3xl p-8 flex flex-col items-center justify-center text-center hover:bg-primary hover:text-white transition-colors group cursor-default shadow-sm border border-slate-100 dark:border-slate-700">
                <MdFoundation className="text-5xl mb-4 text-primary group-hover:text-white" />
                <span className="font-bold uppercase tracking-wider text-xs">{h.solutions.services.industries.civil}</span>
              </div>
            </div>
          </ScrollAnimation>
        </div>
      </section>

      {/* Why Us / Commitment Section */}
      <section className="py-20 md:py-32 bg-background-light dark:bg-background-dark overflow-hidden" id="why-us">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <ScrollAnimation animation="fade-left" className="grid lg:grid-cols-2 gap-12 lg:gap-24 items-center mb-16 lg:mb-32">
            <div className="relative">
              <img alt="Precision Engineering" className="rounded-3xl shadow-4xl w-full object-cover aspect-[4/5]" src={h.commitment.image} />
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-48 h-48 border-2 border-white/30 rounded-full animate-pulse"></div>
            </div>
            <div className="space-y-8 md:space-y-10">
              <h2 className={`text-sm font-extrabold text-accent ${isRTL ? '' : 'uppercase tracking-[0.3em]'} text-center lg:text-left`}>
                {h.commitment.label}
              </h2>
              <h3 className="text-3xl md:text-5xl lg:text-6xl font-black text-slate-900 dark:text-white leading-tight text-center lg:text-left">
                {h.commitment.title}
              </h3>
              <p className="text-base md:text-xl text-slate-600 dark:text-slate-400 font-light leading-relaxed text-center lg:text-left">
                {h.commitment.description}
              </p>
              <div className="flex flex-wrap justify-center lg:justify-start gap-8 md:gap-12">
                <div className="flex flex-col items-center lg:items-start">
                  <span className="text-4xl font-black text-primary">ASME</span>
                  <span className={`text-[10px] font-bold ${isRTL ? '' : 'uppercase tracking-widest'} text-slate-400`}>
                    {h.commitment.standards.asme}
                  </span>
                </div>
                <div className="flex flex-col items-center lg:items-start">
                  <span className="text-4xl font-black text-primary">ASTM</span>
                  <span className={`text-[10px] font-bold ${isRTL ? '' : 'uppercase tracking-widest'} text-slate-400`}>
                    {h.commitment.standards.astm}
                  </span>
                </div>
                <div className="flex flex-col items-center lg:items-start">
                  <span className="text-4xl font-black text-primary">ISO</span>
                  <span className={`text-[10px] font-bold ${isRTL ? '' : 'uppercase tracking-widest'} text-slate-400`}>
                    {h.commitment.standards.iso}
                  </span>
                </div>
                <div className="flex flex-col items-center lg:items-start">
                  <span className="text-4xl font-black text-primary">DIN</span>
                  <span className={`text-[10px] font-bold ${isRTL ? '' : 'uppercase tracking-widest'} text-slate-400`}>
                    {h.commitment.standards.din}
                  </span>
                </div>
              </div>
            </div>
          </ScrollAnimation>
          <ScrollAnimation animation="fade-up" delay={200} className="bg-gradient-to-br from-teal-900 to-teal-800 rounded-[3rem] p-10 md:p-12 lg:p-24 text-white relative overflow-hidden">
            <div className="absolute top-0 right-0 w-96 h-96 bg-secondary/10 rounded-full blur-[100px] -translate-y-1/2 translate-x-1/2"></div>
            <div className="relative z-10 flex flex-col lg:flex-row items-center justify-between gap-16">
              <div className="max-w-2xl text-center lg:text-left">
                <h4 className="text-3xl md:text-4xl lg:text-5xl font-black mb-6 md:mb-8">
                  {h.vision2030.title}
                </h4>
                <p className="text-base md:text-xl text-emerald-50 opacity-90 leading-relaxed font-light">
                  {h.vision2030.text}
                </p>
              </div>
              <div className="flex-shrink-0 flex flex-col items-center mt-8 lg:mt-0">
                <img alt="Saudi Vision 2030 Logo" className="h-32 mb-8 brightness-0 invert" src={h.vision2030.logo} />
                <div className="h-px w-full bg-white/20 mb-8"></div>
                <p className="text-xs font-bold uppercase tracking-[0.4em] opacity-60">{h.vision2030.partner}</p>
              </div>
            </div>
          </ScrollAnimation>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-32 bg-slate-900 text-white" id="contact">
        <ScrollAnimation animation="fade-up" className="max-w-5xl mx-auto px-6 text-center">
          <h2 className="text-5xl md:text-6xl font-black mb-10 leading-tight">
            {h.contact.titleLine1} <br />
            <span className="text-secondary">{h.contact.titleEmphasis}</span>
          </h2>
          <p className="text-xl text-slate-400 mb-16 max-w-2xl mx-auto leading-relaxed">
            {h.contact.subtitle}
          </p>
          <div className="flex flex-col sm:flex-row gap-8 justify-center items-center">
            <Link to="/contact" className="w-full sm:w-auto justify-center px-12 py-6 bg-accent text-white rounded-full font-black text-xl hover:scale-105 transition-transform flex items-center gap-3 shadow-2xl">
              <MdSend />
              {h.contact.cta1}
            </Link>
            <Link to="/contact" className="w-full sm:w-auto justify-center px-12 py-6 border-2 border-slate-700 hover:bg-slate-800 text-white rounded-full font-black text-xl transition-all flex items-center gap-3">
              <MdCall />
              {h.contact.cta2}
            </Link>
          </div>
        </ScrollAnimation>
      </section>

      <Footer />
    </div>
  );
};

export default Home;
